import { useState, useEffect } from 'react';
import {
    Select,
    MenuItem,
    InputLabel,
    Grid2,
    Box,
    FormControlLabel,
    Typography,
    Checkbox,
    Stack,
    Alert,
    Button,
    DialogActions,
    DialogContent,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form-v7';
import moment from 'moment';
import { withStyles } from '@mui/styles';
import ConnectedDatePicker from './ConnectedDatePicker';
import { COLORS, DATE_FORMATS } from '../../../utils/Application_Constants';
import {
    rateTypesEnum,
    getRateTypeKeyFromApiValue,
    rateTypesEnumApi,
} from '../../Configurations/processedTimesheetsConfig';
import formatDate from '../../../utils/formatDate';
import ProcessedTimesheetLineDetailEditInput from './ProcessedTimesheetLineDetailEditInput';
import usePostProcessedTimesheetEntry from '../../hooks/usePostProcessedTimesheetEntry';
import usePatchProcessedTimeSheetEntry from '../../hooks/usePatchProcessedTimesheetEntry';
import { useToast } from '../../Contexts/ToastContext';
import { useProcessedTimesheetContext } from '../../Contexts/ProcessedTimesheetContext';
import { trimLeadingAndTrailingQuotes } from '../../../utils/helpers';
import {
    numberValidation,
    integerValidation,
    currencyValidation,
    fourDecimalNumberValidation,
} from '../../../utils/formValidations';
import {
    MILEAGE_BILLABLE_VALIDATION_MESSAGE,
    getInvalidMileageBillableField,
} from '../../Utils/validatePtsDetailCalculations';

const styles = () => ({
    amountInputs: {
        '& input': {
            paddingLeft: '0 !important',
        },
    },
    checkboxLabel: {
        '& .MuiTypography-root': {
            fontSize: 14,
            color: COLORS.LT_MIDNIGHT,
        },
    },
    select: {
        border: `1px solid ${COLORS.LT_MIDNIGHT10} !important`,
        borderRadius: '4px',
        marginRight: '16px',
        marginBottom: '24px',
        fontSize: 14,

        '&. MuiTypography-caption': {
            position: 'absolute',
            top: '100%',
            marginleft: 0,
        },
    },
    datePicker: {
        '& .MuiPickersSectionList-root': {
            padding: '13px 0',
            fontWeight: 'normal',
            fontSize: 14,
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: COLORS.LT_MIDNIGHT10,
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            position: 'absolute',
            top: '100%',
        },
    },
    dollarInput: {
        '& .MuiInputBase-input': {
            paddingLeft: 0,
        },
    },
});

const MAX_PER_CONSULT_TIME = 99;

const isPerConsultRateType = (rateTypeId, availableRates = []) => {
    if (!rateTypeId) return false;

    const numericRateTypeId = Number(rateTypeId);
    const rateType = availableRates.find((rate) => rate.id === numericRateTypeId);
    if (!rateType) return false;

    return rateType.displayName?.toLowerCase()?.includes('per consult');
};

function ProcessedTimesheetLineDetailForm({
    data,
    availableRates,
    classes,
    onClose,
    newEntry = false,
    processedTimesheetId,
    locations = [],
    isAggregatePts,
    weekEndingStartDate,
    weekEndingEndDate,
    refetch = () => {},
}) {
    const [submissionError, setSubmissionError] = useState(null);
    const [isMileage, setIsMileage] = useState(data?.isMileage || false);
    const [isPerConsult, setIsPerConsult] = useState(false);
    const { showSuccessToast } = useToast();
    const { handleOutdatedError } = useProcessedTimesheetContext();

    const { mutate: postProcessedTimesheetEntry, isLoading: postProcessedTimesheetEntryLoading } =
        usePostProcessedTimesheetEntry({
            onMutate: () => {
                setSubmissionError(null);
            },
            onSuccess: () => {
                refetch();
                showSuccessToast('Entry created successfully.', {
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                });
                setTimeout(() => {
                    onClose();
                }, 2000);
            },
            onError: (error) => {
                handleOutdatedError(error, () => {
                    console.error('usePostProcessedTimesheetEntry:', error);
                    const errorMessage =
                        error?.message && typeof error?.message === 'string'
                            ? trimLeadingAndTrailingQuotes(error?.message)
                            : 'There was an issue creating the entry. Please try again.';
                    setSubmissionError(errorMessage);
                });
            },
        });

    const { mutate: patchProcessedTimesheetEntry, isLoading: patchProcessedTimesheetEntryLoading } =
        usePatchProcessedTimeSheetEntry({
            onMutate: () => {
                setSubmissionError(null);
            },
            onSuccess: () => {
                refetch();
                showSuccessToast('Entry updated successfully.', {
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                });
                setTimeout(() => {
                    onClose();
                }, 2000);
            },
            onError: (error) => {
                handleOutdatedError(error, () => {
                    console.error('usePostProcessedTimesheetEntry:', error);
                    const errorMessage =
                        error?.message && typeof error?.message === 'string'
                            ? trimLeadingAndTrailingQuotes(error?.message)
                            : 'There was an issue updating the entry. Please try again.';
                    setSubmissionError(errorMessage);
                });
            },
        });

    const initializeFormData = (initData) => {
        // Default to single location if only one exists, otherwise empty string for "All"
        const defaultLocationId = locations.length === 1 ? locations[0].locationId : '';

        if (initData == null || Object.keys(initData).length === 0) {
            return {
                date: null,
                rateTypeId: '',
                payAmount: '',
                billAmount: '',
                payRate: '',
                billRate: '',
                payTime: '',
                billTime: '',
                billUnit: '',
                payUnit: '',
                processedAsHoliday: false,
                aB5Amount: '',
                rebateAmount: '',
                discountAmount: '',
                created: null,
                createdBy: null,
                modified: null,
                modifiedBy: null,
                locationId: defaultLocationId,
            };
        }

        const isPerConsultInitData = isPerConsultRateType(initData.rateTypeId, availableRates);
        const payTime = initData.payTime?.toFixed(isPerConsultInitData ? 0 : 2);
        const billTime = initData.billTime?.toFixed(isPerConsultInitData ? 0 : 2);

        return {
            date: initData.date ? moment(initData.date) : null,
            rateTypeId: initData.rateTypeId || '',
            payAmount: initData.payAmount?.toFixed(2) || 0,
            billAmount: initData.billAmount?.toFixed(2) || 0,
            payRate: initData.payRate ?? 0,
            billRate: initData.billRate ?? 0,
            payTime: payTime || 0,
            billTime: billTime || 0,
            billUnit: getRateTypeKeyFromApiValue(initData.billUnit) ?? '',
            payUnit: getRateTypeKeyFromApiValue(initData.payUnit) ?? '',
            processedAsHoliday: initData.processedAsHoliday,
            aB5Amount: initData.aB5Amount?.toFixed(2) || '',
            rebateAmount: initData.rebateAmount?.toFixed(2) || '',
            discountAmount: initData.discountAmount?.toFixed(2) || '',
            created: initData.created,
            createdBy: initData.createdBy,
            modified: initData.modified,
            modifiedBy: initData.modifiedBy,
            locationId: initData?.locationId ?? defaultLocationId,
            lastModified: initData?.lastModified ?? null,
        };
    };

    const {
        control,
        handleSubmit,
        watch,
        clearErrors,
        formState: { isDirty, isValid },
        trigger,
    } = useForm({ defaultValues: initializeFormData(data), mode: 'onBlur' });
    const formValues = watch();
    useEffect(() => {
        if (!newEntry) return;
        const dataKeys = Object.keys(data);
        // if data is not empty, trigger validation (to enable submit button on duplicated entries). The queueMicrotask is to await for the form to be mounted.
        if (dataKeys.length !== 0) queueMicrotask(() => trigger(undefined, { shouldFocus: true }));
    }, [newEntry, data]);

    const returnIsBillableMileage = (rateTypeId) => {
        if (!rateTypeId) return false;
        const numericRateTypeId = Number(rateTypeId);
        const rateType = availableRates.find((rate) => rate.id === numericRateTypeId);
        if (rateType && rateType.displayName.toLowerCase().includes('billable')) {
            return true;
        }
        return false;
    };

    const isMileageBillable = isMileage && returnIsBillableMileage(formValues.rateTypeId);

    // set isMileage to true if rate type is mileage
    useEffect(() => {
        if (formValues.rateTypeId) {
            if (!formValues.rateTypeId) return;
            const numericRateTypeId = Number(formValues.rateTypeId);
            const rateType = availableRates.find((rate) => rate.id === numericRateTypeId);
            if (rateType.displayName.toLowerCase().includes('mileage')) {
                setIsMileage(true);
            } else {
                setIsMileage(false);
                clearErrors('billUnit');
            }
        }
    }, [formValues.rateTypeId]);

    // set isPerConsult to true if rate type is per consult
    useEffect(() => {
        if (formValues.rateTypeId) {
            if (isPerConsultRateType(formValues.rateTypeId, availableRates)) {
                setIsPerConsult(true);
            } else {
                setIsPerConsult(false);
                clearErrors('payTime');
                clearErrors('billTime');
            }
        }
    }, [formValues.rateTypeId]);

    function returnRateTypeDisplay(id) {
        const returnRateType = availableRates.find((type) => type.id === id);
        return returnRateType ? returnRateType.displayName : '';
    }

    const onSubmit = (formData) => {
        setSubmissionError(null);
        const entryFormData = {
            ...formData,
            lastModified: formData.lastModified,
            billAmount: formData.billAmount ? parseFloat(formData.billAmount) : null,
            billRate: formData.billRate ? parseFloat(formData.billRate) : null,
            payAmount: formData.payAmount ? parseFloat(formData.payAmount) : null,
            payRate: formData.payRate ? parseFloat(formData.payRate) : null,
            billTime: formData.billTime ? parseFloat(formData.billTime) : null,
            payTime: formData.payTime ? parseFloat(formData.payTime) : null,
            aB5Amount: formData.aB5Amount ? parseFloat(formData.aB5Amount) : null,
            rebateAmount: formData.rebateAmount ? parseFloat(formData.rebateAmount) : null,
            discountAmount: formData.discountAmount ? parseFloat(formData.discountAmount) : null,
            processedTimesheetId: parseFloat(processedTimesheetId),
            locationId:
                formData?.locationId && formData.locationId !== '' ? formData.locationId : null,
            rateTypeId: formData.rateTypeId ? formData.rateTypeId.toString() : null,
            billUnit: formData.billUnit ? rateTypesEnumApi[formData.billUnit] : null,
            payUnit: formData.payUnit ? rateTypesEnumApi[formData.payUnit] : null,
        };
        const mileageEntryFormData = {
            lastModified: formData.lastModified,
            processedTimesheetId: parseFloat(processedTimesheetId),
            date: formData.date,
            locationId:
                formData?.locationId && formData.locationId !== '' ? formData.locationId : null,
            rateTypeId: formData.rateTypeId ? formData.rateTypeId.toString() : null,
            payAmount: formData.payAmount ? parseFloat(formData.payAmount) : null,
            billAmount: formData.billAmount ? parseFloat(formData.billAmount) : null,
            rate: formData.payRate ? formData.payRate.toString() : null,
            payMiles: formData.payTime ? parseFloat(formData.payTime) : null,
            billMiles: formData.billTime ? parseFloat(formData.billTime) : null,
            rateUnit: formData.billUnit ? rateTypesEnumApi[formData.billUnit] : null,
            isBillable: returnIsBillableMileage(formData.rateTypeId),
            isMileage: true,
        };
        const newFormData = isMileage ? mileageEntryFormData : entryFormData;
        newFormData.date = formatDate(newFormData.date, { format: 'YYYY-MM-DDTHH:mm:ss' });
        if (newEntry) {
            postProcessedTimesheetEntry(newFormData);
        } else {
            newFormData.id = data.id ?? null;
            patchProcessedTimesheetEntry(newFormData);
        }
    };

    function returnRateTypeCode(rateTypeId) {
        if (!rateTypeId) return '';
        const numericId = Number(rateTypeId);
        const rateType = availableRates.find((rate) => rate.id === numericId);
        return rateType ? rateType.rateCode : '';
    }

    const handleFormSubmit = () => {
        handleSubmit(onSubmit, () => {
            setSubmissionError('Please fix the errors above before submitting.');
        })();
    };

    return (
        <>
            <DialogContent sx={{ padding: '0px' }}>
                <Grid2 container>
                    <Grid2 size={6}>
                        <Box sx={{ mr: 2 }}>
                            <InputLabel sx={{ fontSize: 14, mb: 1 }} id="transaction-date-label">
                                Transaction Date <span style={{ color: 'red' }}>*</span>
                            </InputLabel>
                            <ConnectedDatePicker
                                control={control}
                                name="date"
                                pickerProps={{
                                    format: 'MM/DD/YYYY',
                                    className: classes.datePicker,
                                    minDate: weekEndingStartDate,
                                    maxDate: weekEndingEndDate,
                                    slotProps: {
                                        textField: {
                                            FormHelperTextProps: {
                                                sx: { position: 'relative !important' },
                                            },
                                        },
                                    },
                                }}
                                dateAdapter={(date) => (date ? date.clone().startOf('day') : date)}
                                rules={{
                                    required: 'Transaction Date is required',
                                    validate: (value) => {
                                        if (
                                            value &&
                                            (value < weekEndingStartDate ||
                                                value > weekEndingEndDate)
                                        ) {
                                            return `Transaction Date must be a date between ${formatDate(
                                                weekEndingStartDate
                                            )} and ${formatDate(weekEndingEndDate)}`;
                                        }
                                        return true;
                                    },
                                }}
                            />
                        </Box>
                    </Grid2>
                    <Grid2 size={6}>
                        <Box sx={{ mr: 2 }}>
                            <InputLabel sx={{ fontSize: 14, mb: 1 }} id="location-id-label">
                                Location{' '}
                                {!isAggregatePts ? <span style={{ color: 'red' }}> *</span> : ''}
                            </InputLabel>
                            <Controller
                                control={control}
                                name="locationId"
                                rules={{
                                    required: isAggregatePts ? false : 'Location is required',
                                }}
                                render={({ field, fieldState }) => (
                                    <Box sx={{ position: 'relative' }}>
                                        <Select
                                            {...field}
                                            labelId="location-id-label"
                                            className={classes.select}
                                            displayEmpty
                                            error={!!fieldState.error}
                                        >
                                            {locations.length > 1 && (
                                                <MenuItem value="" sx={{ fontSize: 14 }}>
                                                    {isAggregatePts ? 'All' : 'Select location'}
                                                </MenuItem>
                                            )}
                                            {locations.map((location) => (
                                                <MenuItem
                                                    key={location.locationId}
                                                    value={location.locationId}
                                                    sx={{ fontSize: 14 }}
                                                >
                                                    {location.locationName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {fieldState.error && (
                                            <Typography
                                                color="error"
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    mt: 0.5,
                                                    position: 'absolute',
                                                    top: '43px',
                                                    marginLeft: 0,
                                                }}
                                            >
                                                {fieldState.error.message}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            />
                        </Box>
                    </Grid2>
                    <Grid2 size={6}>
                        <Box sx={{ mr: 2 }}>
                            <InputLabel sx={{ fontSize: 14, mb: 1 }} id="rate-type-label">
                                Rate Type <span style={{ color: 'red' }}>*</span>
                            </InputLabel>
                            <Controller
                                control={control}
                                name="rateTypeId"
                                rules={{ required: 'Rate Type is required' }}
                                render={({ field, fieldState: { error: rateError } }) => (
                                    <Box sx={{ position: 'relative' }}>
                                        <Select
                                            {...field}
                                            labelId="rate-type-label"
                                            className={classes.select}
                                            displayEmpty
                                            error={!!rateError}
                                            renderValue={(selected) =>
                                                returnRateTypeDisplay(parseFloat(selected))
                                            }
                                        >
                                            {availableRates.map((type, index) => (
                                                <MenuItem
                                                    // eslint-disable-next-line
                                                    key={index}
                                                    value={type.id}
                                                    sx={{ fontSize: 14 }}
                                                >
                                                    {type.displayName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {rateError && (
                                            <Typography
                                                color="error"
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    mt: 0.5,
                                                    position: 'absolute',
                                                    top: '43px',
                                                    marginLeft: 0,
                                                }}
                                            >
                                                {rateError.message}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            />
                        </Box>
                    </Grid2>
                    <Grid2 size={6}>
                        <ProcessedTimesheetLineDetailEditInput
                            label="Pay Type ID"
                            name="rateTypeCode"
                            disabled
                            value={returnRateTypeCode(formValues.rateTypeId)}
                            control={control}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <ProcessedTimesheetLineDetailEditInput
                            label="Pay Amount"
                            control={control}
                            name="payAmount"
                            dollarInput
                            required
                            rules={{
                                ...currencyValidation,
                                validate: (value = 0, context = {}) => {
                                    const isValidCurrency = currencyValidation.validate(value);
                                    if (isValidCurrency !== true) return isValidCurrency;
                                    const { payRate, payTime } = context;
                                    if (payRate && payTime) {
                                        // Add a small number to work around a floating point precision issue. See here for more info:
                                        // https://stackoverflow.com/questions/10015027/tofixed-is-not-rounding-in-javascript/32605063#comment140640805_10015409
                                        const calculatedPayAmount = (
                                            payRate * payTime +
                                            0.00001
                                        ).toFixed(2);
                                        if (calculatedPayAmount !== Number(value).toFixed(2)) {
                                            return `Pay Amount does not match the calculated amount: "${calculatedPayAmount}".`;
                                        }
                                    }
                                    return true;
                                },
                                deps: ['billAmount'], // Re-validates billAmount for mileage-billable checks
                            }}
                            InputProps={{}}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <ProcessedTimesheetLineDetailEditInput
                            label="Bill Amount"
                            control={control}
                            name="billAmount"
                            dollarInput
                            required
                            rules={{
                                ...currencyValidation,
                                validate: (value = 0, context = {}) => {
                                    const isValidCurrency = currencyValidation.validate(value);
                                    if (isValidCurrency !== true) return isValidCurrency;
                                    const { billRate, payRate, billTime, payTime, payAmount } =
                                        context;
                                    // For mileage the Bill Rate is not shown and must match the on-screen Pay Rate.
                                    const effectiveBillRate = isMileage ? payRate : billRate;
                                    if (effectiveBillRate && billTime) {
                                        // Add a small number to work around a floating point precision issue. See here for more info:
                                        // https://stackoverflow.com/questions/10015027/tofixed-is-not-rounding-in-javascript/32605063#comment140640805_10015409
                                        const calculatedBillAmount = (
                                            effectiveBillRate * billTime +
                                            0.00001
                                        ).toFixed(2);
                                        if (calculatedBillAmount !== Number(value).toFixed(2)) {
                                            return `Bill Amount does not match the calculated amount: "${calculatedBillAmount}".`;
                                        }
                                    }
                                    if (isMileageBillable) {
                                        const invalidField = getInvalidMileageBillableField({
                                            payUnits: payTime,
                                            billUnits: billTime,
                                            payAmount,
                                            billAmount: value,
                                        });
                                        if (invalidField === 'billAmount') {
                                            return MILEAGE_BILLABLE_VALIDATION_MESSAGE;
                                        }
                                    }
                                    return true;
                                },
                            }}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <ProcessedTimesheetLineDetailEditInput
                            label="Pay Rate"
                            required
                            control={control}
                            name="payRate"
                            rules={
                                isMileage
                                    ? {
                                          ...fourDecimalNumberValidation,
                                          // For mileage the Pay Rate also drives the Bill Amount, so re-validate both.
                                          deps: ['payAmount', 'billAmount'],
                                      }
                                    : { ...numberValidation, deps: ['payAmount'] }
                            }
                        />
                    </Grid2>
                    {!isMileage && (
                        <Grid2 size={6}>
                            <ProcessedTimesheetLineDetailEditInput
                                label="Bill Rate"
                                required
                                control={control}
                                name="billRate"
                                rules={{
                                    ...numberValidation,
                                    deps: ['billAmount'], // Re-validates billAmount on changes
                                }}
                            />
                        </Grid2>
                    )}
                    <Grid2 size={6}>
                        <ProcessedTimesheetLineDetailEditInput
                            label={isMileage ? 'Pay Miles' : 'Pay Time'}
                            required
                            control={control}
                            name="payTime"
                            rules={{
                                ...(isPerConsult
                                    ? integerValidation({ max: MAX_PER_CONSULT_TIME })
                                    : numberValidation),
                                deps: ['payAmount', 'billTime'], // Re-validates payAmount and billTime (mileage-billable) on changes
                            }}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <ProcessedTimesheetLineDetailEditInput
                            label={isMileage ? 'Bill Miles' : 'Bill Time'}
                            required
                            control={control}
                            name="billTime"
                            rules={{
                                ...(isPerConsult
                                    ? integerValidation({ max: MAX_PER_CONSULT_TIME })
                                    : numberValidation),
                                validate: (value, context = {}) => {
                                    const baseValidation = isPerConsult
                                        ? integerValidation({
                                              max: MAX_PER_CONSULT_TIME,
                                          }).validate(value)
                                        : numberValidation.validate(value);
                                    if (baseValidation !== true) return baseValidation;
                                    if (isMileageBillable) {
                                        const { payTime, payAmount, billAmount } = context;
                                        const invalidField = getInvalidMileageBillableField({
                                            payUnits: payTime,
                                            billUnits: value,
                                            payAmount,
                                            billAmount,
                                        });
                                        if (invalidField === 'billUnits') {
                                            return MILEAGE_BILLABLE_VALIDATION_MESSAGE;
                                        }
                                    }
                                    return true;
                                },
                                deps: ['billAmount'], // Re-validates billAmount on changes
                            }}
                        />
                    </Grid2>
                    {!isMileage && (
                        <Grid2 size={6}>
                            <Box sx={{ mr: 2 }}>
                                <InputLabel sx={{ fontSize: 14, mb: 1 }} id="pay-unit-label">
                                    Pay Unit
                                </InputLabel>
                                <Controller
                                    control={control}
                                    name="payUnit"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            labelId="pay-unit-label"
                                            className={classes.select}
                                            displayEmpty
                                            renderValue={(selected) =>
                                                selected ? rateTypesEnum[selected] : ''
                                            }
                                        >
                                            <MenuItem value="" sx={{ fontSize: 14 }}>
                                                None
                                            </MenuItem>
                                            {Object.keys(rateTypesEnum).map((type) => (
                                                <MenuItem
                                                    key={type}
                                                    value={type}
                                                    sx={{ fontSize: 14 }}
                                                >
                                                    {rateTypesEnum[type]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </Box>
                        </Grid2>
                    )}
                    <Grid2 size={6}>
                        <Box sx={{ mr: 2 }}>
                            <InputLabel sx={{ fontSize: 14, mb: 1 }} id="bill-unit-label">
                                {isMileage ? 'Unit' : 'Bill Unit'}
                                {isMileage && <span style={{ color: 'red' }}> *</span>}
                            </InputLabel>
                            <Controller
                                control={control}
                                name="billUnit"
                                rules={{ ...(isMileage ? { required: 'Unit is required' } : {}) }}
                                render={({ field, fieldState }) => (
                                    <Box sx={{ position: 'relative' }}>
                                        <Select
                                            {...field}
                                            labelId="bill-unit-label"
                                            className={classes.select}
                                            displayEmpty
                                            error={!!fieldState.error}
                                            renderValue={(selected) =>
                                                selected ? rateTypesEnum[selected] : ''
                                            }
                                        >
                                            <MenuItem value="" sx={{ fontSize: 14 }}>
                                                None
                                            </MenuItem>
                                            {Object.keys(rateTypesEnum).map((type) => {
                                                if (
                                                    isMileage &&
                                                    (type === 'perWeek' ||
                                                        type === 'perBookingDay' ||
                                                        type === 'each')
                                                ) {
                                                    return (
                                                        <MenuItem
                                                            key={type}
                                                            value={type}
                                                            sx={{ fontSize: 14 }}
                                                        >
                                                            {rateTypesEnum[type]}
                                                        </MenuItem>
                                                    );
                                                    // eslint-disable-next-line
                                                } else if (!isMileage) {
                                                    return (
                                                        <MenuItem
                                                            key={type}
                                                            value={type}
                                                            sx={{ fontSize: 14 }}
                                                        >
                                                            {rateTypesEnum[type]}
                                                        </MenuItem>
                                                    );
                                                } else return null;
                                            })}
                                        </Select>
                                        {fieldState.error && (
                                            <Typography
                                                color="error"
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    mt: 0.5,
                                                    position: 'absolute',
                                                    top: '43px',
                                                    marginLeft: 0,
                                                }}
                                            >
                                                {fieldState.error.message}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            />
                        </Box>
                    </Grid2>
                    {!isMileage && (
                        <>
                            <Grid2 size={6}>
                                <ProcessedTimesheetLineDetailEditInput
                                    label="AB5 Amount"
                                    control={control}
                                    name="aB5Amount"
                                    dollarInput
                                    rules={currencyValidation}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <ProcessedTimesheetLineDetailEditInput
                                    label="Rebate Amount"
                                    control={control}
                                    name="rebateAmount"
                                    dollarInput
                                    rules={currencyValidation}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <ProcessedTimesheetLineDetailEditInput
                                    label="Discount Amount"
                                    control={control}
                                    name="discountAmount"
                                    dollarInput
                                    rules={currencyValidation}
                                />
                            </Grid2>
                            <Grid2 size={6} />
                        </>
                    )}
                    {!isMileage && (
                        <Grid2 size={12}>
                            <Box sx={{ mr: 2, mb: 2 }}>
                                <FormControlLabel
                                    className={classes.checkboxLabel}
                                    control={
                                        <Controller
                                            control={control}
                                            name="processedAsHoliday"
                                            render={({ field }) => (
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value}
                                                    onChange={(e) =>
                                                        field.onChange(e.target.checked)
                                                    }
                                                />
                                            )}
                                        />
                                    }
                                    label="Processed As Holiday"
                                />
                            </Box>
                        </Grid2>
                    )}
                    {!newEntry && (
                        <>
                            <Grid2 size={6} mb={3}>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        color={COLORS.LT_MIDNIGHT}
                                        mb={1}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {`Created${formValues.createdBy ? ' By' : ''}`}
                                    </Typography>
                                    <Typography variant="body2" color={COLORS.LT_MIDNIGHT75}>
                                        {`${
                                            formValues.createdBy ? `${formValues.createdBy}, ` : ''
                                        }${
                                            formValues.created
                                                ? `${formatDate(formValues.created, {
                                                      format: DATE_FORMATS.LT_DATE_AND_TIME,
                                                  })}`
                                                : '-'
                                        }`}
                                    </Typography>
                                </Box>
                            </Grid2>
                            <Grid2 size={6}>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        color={COLORS.LT_MIDNIGHT}
                                        mb={1}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {`Modified${formValues.modifiedBy ? ' By' : ''}`}
                                    </Typography>
                                    <Typography variant="body2" color={COLORS.LT_MIDNIGHT75}>
                                        {`${
                                            formValues.modifiedBy
                                                ? `${formValues.modifiedBy}, `
                                                : ''
                                        }${
                                            formValues.modified
                                                ? `${formatDate(formValues.modified, {
                                                      format: DATE_FORMATS.LT_DATE_AND_TIME,
                                                  })}`
                                                : '-'
                                        }`}
                                    </Typography>
                                </Box>
                            </Grid2>
                        </>
                    )}
                </Grid2>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-start', px: 0 }}>
                <Stack direction="column" width="100%" gap={2}>
                    {submissionError && (
                        <Typography color="error" variant="body2">
                            {submissionError}
                        </Typography>
                    )}
                    {isDirty && <Alert severity="warning">You have unsaved changes.</Alert>}
                    <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Button variant="outlined" onClick={onClose}>
                            {isDirty ? 'Discard changes' : 'Cancel'}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleFormSubmit}
                            loading={
                                postProcessedTimesheetEntryLoading ||
                                patchProcessedTimesheetEntryLoading
                            }
                            disabled={
                                !(isDirty || (isValid && newEntry)) || // do not disable button if form is new and isValid (even if there are no changes)
                                postProcessedTimesheetEntryLoading ||
                                patchProcessedTimesheetEntryLoading
                            }
                        >
                            Save changes
                        </Button>
                    </Stack>
                </Stack>
            </DialogActions>
        </>
    );
}

export default withStyles(styles)(ProcessedTimesheetLineDetailForm);
